import { useWindowSize } from 'common'
import { ProductLocationModel } from 'interfaces'
import { observer } from 'mobx-react-lite'
import { ReactElement, useCallback, useRef } from 'react'
import { ISeller } from '../../../services/MultiSellerServicesProvider'
import styled from 'styled-components'
import { ImageBypartAPIResponse } from '../../../api/searchResults/interfaces'
import { CartProduct, IBundleProduct, useCartProduct } from 'cart'
import { OrderSelection } from '../../../store/models/ShoppingCartModels'
import { Vehicle } from '../../../store/models/Vehicles'
import { StoreInstances } from '../../../store/StoreInstancesContainer'
import { breakpoints } from '../../../theme/breakpoints'
import { defaultTheme } from '../../../theme/index'
import { Thumbnail } from '../../../ui-components/products/Thumbnail'
import { CartItemTable } from './CartItemTable'
import {
  hasNonPrimaryLocationError,
  hasOutOfStockError,
} from './AvailabilityErrors/utlis'

interface CartItemRowProps {
  product: CartProduct
  orderSelection: OrderSelection
  vehicle: Vehicle
  partImages: ImageBypartAPIResponse[]
  sellerDetails?: ISeller
  bundledProduct: IBundleProduct
}

const nonPrimaryLocationRowStyle = {
  backgroundColor: defaultTheme.colors.primaryFadeAlt,
  boxShadow: `inset 10px 0px 0px ${defaultTheme.colors.primary}`,
}

export const CartItemRow = observer((props: CartItemRowProps): ReactElement => {
  const {
    product,
    orderSelection,
    vehicle,
    partImages,
    sellerDetails,
    bundledProduct,
  } = props

  const setInputValueRef = useRef<(val: string) => void>(null)

  const {
    priceSpinner,
    showLocationSelector,
    updateQuantity,
    initialSelectorQty,
    setPriceSpinner,
    currentQty,
  } = useCartProduct({
    product,
    vehicle,
    sellerDetails,
  })

  const currentQtyRef = useRef(currentQty)
  currentQtyRef.current = currentQty

  const updateQuantityLocal = useCallback(
    (newQty: number, activeLocation: ProductLocationModel) => {
      const totalQty = product.quantityRequested
      const qurrentQuantity = orderSelection.quantityRequested
      const updatedQty = totalQty - (qurrentQuantity - newQty)
      return updateQuantity(updatedQty, activeLocation).then((result) => {
        if (setInputValueRef.current) {
          setInputValueRef.current(currentQtyRef.current.toString())
        }
        return result
      })
    },
    [
      orderSelection.quantityRequested,
      product.quantityRequested,
      updateQuantity,
    ]
  )

  const activeLocation = product.location?.find(
    (l) => l.locationId.toString() === orderSelection?.locationId?.toString()
  )

  // Calculate price based on the activeLocation from product (updated from cart store)
  const calculateYourCostAfterPriceBreaks = (): number => {
    if (!activeLocation) return 0
    const priceBreaks = activeLocation?.priceBreaks || []
    const qty = orderSelection?.quantityRequested
    if (priceBreaks.length > 0) {
      const lastPriceBreak = priceBreaks[priceBreaks.length - 1]
      for (const priceBreak of priceBreaks || []) {
        if (priceBreak?.minQty <= qty && priceBreak?.maxQty >= qty) {
          return priceBreak?.unitCost
        }
        if (lastPriceBreak?.minQty && qty >= lastPriceBreak?.minQty) {
          return lastPriceBreak?.unitCost
        }
      }
    }
    return activeLocation.cost
  }

  const windowWidth = useWindowSize()
  const isMobileView = Number(windowWidth.width) <= breakpoints.mobileLarge

  const thumbnail = product?.partImages?.length
    ? product.partImages.find((img) => img.type === 'thumbnail')?.url
    : ''

  const getImageUrl = (lineCode, partNumber): string => {
    const part = partImages?.find(
      (item) => item.partNumber === partNumber && item.linecode === lineCode
    )
    if (part) {
      return part.partImages[0]?.url
    }
    return null
  }

  const validatePrimaryLocation =
    StoreInstances.userStore.preferences.cart_nonPrimaryLocWarningEnabled?.toLowerCase() ===
    'true'

  const productHasNonPrimaryLocationError =
    hasNonPrimaryLocationError(product) && validatePrimaryLocation

  const productIsOutOfStock = hasOutOfStockError(product)

  // MPV3- 3001 Added non primary location warning when prices are updated
  const nonPrimaryLocSelected = activeLocation?.locationId?.toString() !== '100'

  const rowStyle =
    productHasNonPrimaryLocationError ||
    nonPrimaryLocSelected ||
    productIsOutOfStock
      ? nonPrimaryLocationRowStyle
      : {}

  const cartItemTableProps = {
    product,
    vehicle,
    location: activeLocation,
    yourCostAfterPriceBreaks: calculateYourCostAfterPriceBreaks,
    currentQty: orderSelection?.quantityRequested,
    priceSpinner,
    showLocationSelector,
    updateQuantity: (newQty: number) => {
      setPriceSpinner(true)
      return updateQuantityLocal(newQty, activeLocation).then(() => {
        // Just to return void
      })
    },
    setInputValueRef,
    initialSelectorQty,
    sellerDetails,
    bundledProduct,
    isMobileView,
  }

  return (
    <Row
      style={rowStyle}
      // data-has-error is used by the ValidationWarningBar 'Review' link
      // to scroll to the first error row in the cart
      data-has-error={
        productHasNonPrimaryLocationError ||
        nonPrimaryLocSelected ||
        productIsOutOfStock
          ? 'true'
          : undefined
      }
    >
      <ImageCell>
        <Cell>
          {thumbnail ? (
            <Thumbnail
              urlOrBase64={thumbnail}
              width={100}
              height={100}
              placeholder
            />
          ) : (
            <Thumbnail
              urlOrBase64={getImageUrl(product.lineCode, product.partNumber)}
              width={100}
              height={100}
              placeholder
            />
          )}
        </Cell>
      </ImageCell>
      {isMobileView ? (
        <MobileContentCell colSpan={8}>
          <CartItemTable {...cartItemTableProps} />
        </MobileContentCell>
      ) : (
        <CartItemTable {...cartItemTableProps} />
      )}
    </Row>
  )
})

const Row = styled.tr`
  background: #fff;
  box-shadow: inset 0px -1px 0px #d3d2cf;
  border: 24px;
  max-width: 100%;
  font-family: Roboto, serif;
  font-weight: bold;
  @media (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    width: 100%;
  }
  @media (min-width: ${breakpoints.mobileLarge + 1}px) {
    .text-visibility {
      display: none;
    }
  }
`

const ImageCell = styled.td`
  vertical-align: top;
  padding: 20px 14px;
  width: 14%;
  @media (max-width: ${breakpoints.mobileLarge}px) {
    width: auto;
    flex-shrink: 0;
    padding: 12px;
  }
`

const MobileContentCell = styled.td`
  @media (max-width: ${breakpoints.mobileLarge}px) {
    display: block;
    flex: 1;
    min-width: 0;
    width: auto;
    padding: 12px 12px 12px 0;
    vertical-align: top;
  }
`

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  font-size: 16px;
  line-height: 24px;
  text-align: start;
  font-weight: bold;
`
